import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import WorkforceOsLayout from "../../../components/workforce_os/WorkforceOsLayout";
import RebecaMiguelChatbot from "../../../components/workforce_os/RebecaMiguelChatbot";
import { Copy, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  fullName: "",
  specialty: "",
  licenseStates: "",
  certifications: "",
  preferredLocations: "",
  availability: "",
  preference: "travel",
  yearsExp: "",
};

function readiness(d) {
  const fields = ["fullName", "specialty", "licenseStates", "certifications", "preferredLocations", "availability", "yearsExp"];
  const filled = fields.filter((f) => (d[f] || "").trim()).length;
  const pct = Math.round((filled / fields.length) * 100);
  return {
    pct,
    label: pct >= 90 ? "Recruiter-ready" : pct >= 60 ? "Almost there" : "Add more details",
  };
}

export default function ClinicianPassport() {
  React.useEffect(() => {
    document.title = "TRITAL Clinician Passport™ | Workforce OS™";
  }, []);
  const [d, setD] = useState(EMPTY);
  const r = useMemo(() => readiness(d), [d]);

  const passport = useMemo(() => {
    return [
      `=== TRITAL CLINICIAN PASSPORT ===`,
      `Name: ${d.fullName || "—"}`,
      `Specialty: ${d.specialty || "—"}`,
      `Years of experience: ${d.yearsExp || "—"}`,
      `License states: ${d.licenseStates || "—"}`,
      `Certifications: ${d.certifications || "—"}`,
      `Preferred locations: ${d.preferredLocations || "—"}`,
      `Availability: ${d.availability || "—"}`,
      `Work preference: ${d.preference}`,
    ].join("\n");
  }, [d]);

  const FIELDS = [
    ["fullName",            "Full Name"],
    ["specialty",           "Specialty"],
    ["yearsExp",            "Years of Experience"],
    ["licenseStates",       "License States (comma-separated)"],
    ["certifications",      "Certifications"],
    ["preferredLocations",  "Preferred Locations"],
    ["availability",        "Start Availability"],
  ];

  return (
    <WorkforceOsLayout
      breadcrumb="Clinician Passport"
      title="TRITAL Clinician Passport™"
      description="Build a free clinician profile readiness summary you can share with TRITAL recruiters in one click. No signup."
    >
      <section className="py-10">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="passport-form">
            <h3 className="font-display font-bold text-lg text-trital-navy">Your Information</h3>
            <div className="mt-4 grid grid-cols-1 gap-3">
              {FIELDS.map(([k, label]) => (
                <div key={k}>
                  <label className="block text-[12px] font-semibold text-slate-700 mb-1">{label}</label>
                  <input
                    value={d[k]}
                    onChange={(e) => setD({ ...d, [k]: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
                    data-testid={`passport-input-${k}`}
                  />
                </div>
              ))}
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1">Work preference</label>
                <select
                  value={d.preference}
                  onChange={(e) => setD({ ...d, preference: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px]"
                  data-testid="passport-input-preference"
                >
                  {["travel", "local", "per diem", "contract", "permanent"].map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-trital-navy text-white p-6" data-testid="passport-preview">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold text-lg">Passport Preview</h3>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(passport);
                    toast.success("Passport copied to clipboard");
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-trital-navy text-[12px] font-semibold"
                  data-testid="passport-copy-btn"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
              <pre className="mt-3 whitespace-pre-wrap font-mono text-[12.5px] text-white/90 leading-relaxed bg-white/5 border border-white/10 rounded-xl p-4 min-h-[260px]">
                {passport}
              </pre>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="overline text-slate-500">Profile Readiness</div>
              <div className="mt-1 flex items-end gap-3">
                <div className="font-display font-black text-5xl tabular-nums text-trital-navy" data-testid="passport-pct">{r.pct}%</div>
                <div className="pb-1.5 text-[13px] text-slate-600">{r.label}</div>
              </div>
              <Link
                to="/careers/jobs"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-trital-red text-white text-[13px] font-semibold"
                data-testid="passport-jobs-cta"
              >
                Find jobs that match <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
              </Link>
            </div>
          </aside>
        </div>
      </section>
      <RebecaMiguelChatbot variant="floating" />
    </WorkforceOsLayout>
  );
}
